import React from 'react';
import { 
  X, 
  Volume2, 
  VolumeX, 
  Printer, 
  MapPin, 
  Calendar, 
  Briefcase, 
  Heart, 
  BookOpen, 
  ChevronRight,
  FileText,
  ExternalLink,
  Layers,
  Users
} from 'lucide-react';
import { FamilyMember, Story, ArchiveItem, ArchiveTab } from '../types';
import { useVoiceReader } from '../context/VoiceReaderContext';

/**
 * Validates that an image path is a verified local/historical artifact,
 * strictly rejecting generic stock images (Unsplash, placeholders, etc.).
 */
export const isVerifiedHistoricalImage = (url?: string): boolean => {
  if (!url || typeof url !== 'string') return false;
  const clean = url.trim().toLowerCase();
  if (!clean) return false;
  if (
    clean.includes('unsplash.com') ||
    clean.includes('images.unsplash') ||
    clean.includes('placeholder') ||
    clean.includes('via.placeholder') ||
    clean.includes('default-avatar') ||
    clean.includes('dummyimage') ||
    clean.includes('stock-photo')
  ) {
    return false;
  }
  return true;
};

/**
 * Extracts initials for the stylish archival initial monogram badge
 */
export const getPersonInitials = (firstName: string, lastName: string): string => {
  const f = (firstName || '').trim().split(/\s+/);
  const l = (lastName || '').trim().split(/\s+/);
  const firstInit = f[0]?.[0] || '';
  const midInit = f.length > 1 ? f[1]?.[0] : '';
  const lastInit = l[l.length - 1]?.[0] || '';
  return `${firstInit}${midInit}${lastInit}`.toUpperCase().slice(0, 3) || 'REC';
};

export interface PersonDossierModalProps {
  member?: FamilyMember;
  person?: FamilyMember;
  allMembers: FamilyMember[];
  allStories?: Story[];
  allArchiveItems?: ArchiveItem[];
  onClose: () => void;
  onSelectMember: (memberId: string) => void;
  onSelectStory?: (storyId: string) => void;
  onSelectTab?: (tab: ArchiveTab) => void;
  textToSpeechEnabled?: boolean;
}

export const PersonDossierModal: React.FC<PersonDossierModalProps> = ({
  member: memberProp,
  person: personProp,
  allMembers = [],
  allStories = [],
  allArchiveItems = [],
  onClose,
  onSelectMember,
  onSelectStory,
  onSelectTab
}) => {
  const activePerson = personProp || memberProp;

  if (!activePerson) {
    return null;
  }

  // Voice narration context
  const { speakText, isPlaying, stop } = useVoiceReader();

  // Historical image resolution
  const candidateImage = activePerson.image || activePerson.photoUrl;
  const hasPortrait = isVerifiedHistoricalImage(candidateImage);
  const verifiedPhotoUrl = hasPortrait ? candidateImage : undefined;

  // Immediate Family Connections
  const father = allMembers.find(m => m.id === activePerson.fatherId);
  const mother = allMembers.find(m => m.id === activePerson.motherId);
  const spouses = allMembers.filter(m => (activePerson.spouseIds || []).includes(m.id));
  const children = allMembers.filter(m => (activePerson.childrenIds || []).includes(m.id));

  // Compute Siblings dynamically if parent IDs are present
  const siblings = allMembers.filter(m => {
    if (m.id === activePerson.id) return false;
    const sameFather = activePerson.fatherId && m.fatherId && m.fatherId === activePerson.fatherId;
    const sameMother = activePerson.motherId && m.motherId && m.motherId === activePerson.motherId;
    return Boolean(sameFather || sameMother);
  });

  // Linked archival items & stories
  const personStories = allStories.filter(s => s.taggedMemberIds?.includes(activePerson.id));
  const personPhotos = allArchiveItems.filter(a => a.taggedMemberIds?.includes(activePerson.id));

  // Life Span formatting
  const formattedLifeSpan = (() => {
    if (activePerson.birthYear && activePerson.deathYear) {
      return `${activePerson.birthYear} – ${activePerson.deathYear}`;
    }
    if (activePerson.birthYear && activePerson.isLiving) {
      return `${activePerson.birthYear} – Present`;
    }
    if (activePerson.birthYear && !activePerson.isLiving) {
      return `b. ${activePerson.birthYear}`;
    }
    if (activePerson.deathYear) {
      return `d. ${activePerson.deathYear}`;
    }
    return null;
  })();

  // Narrator toggle
  const handleToggleSpeakBio = () => {
    if (isPlaying) {
      stop();
    } else {
      const birthSnippet = activePerson.birthDate 
        ? `Born ${activePerson.birthDate}` 
        : activePerson.birthYear ? `Born ${activePerson.birthYear}` : '';
      const placeSnippet = activePerson.birthPlace ? ` in ${activePerson.birthPlace}.` : '.';
      const textToSpeak = `${activePerson.firstName} ${activePerson.lastName}. ${birthSnippet}${placeSnippet} ${activePerson.bio || ''}`;
      speakText(textToSpeak, `Dossier: ${activePerson.firstName} ${activePerson.lastName}`, `person-bio-${activePerson.id}`);
    }
  };

  const handlePrintDossier = () => {
    window.print();
  };

  const initials = getPersonInitials(activePerson.firstName, activePerson.lastName);

  // Check whether any family connections exist
  const hasAnyConnections = Boolean(
    father || mother || spouses.length > 0 || children.length > 0 || siblings.length > 0
  );

  // Check whether source lineage items exist
  const sourceRefs = activePerson.sourceReferences || [];
  const hasSourceLineage = sourceRefs.length > 0 || personStories.length > 0 || personPhotos.length > 0;

  return (
    <div 
      className="fixed inset-0 z-50 bg-black/75 backdrop-blur-xs flex items-center justify-center p-3 sm:p-4 md:p-6 overflow-y-auto"
      role="dialog"
      aria-modal="true"
      aria-labelledby="dossier-title"
    >
      <div className="bg-[#FAF8F5] border-4 border-[#2C221E] rounded-2xl max-w-4xl w-full max-h-[92vh] overflow-y-auto shadow-2xl relative text-[#2C221E]">
        
        {/* Top Header Bar */}
        <div className="bg-[#2C221E] text-white p-3.5 sm:p-5 sticky top-0 z-20 flex flex-wrap items-center justify-between gap-3 no-print border-b-2 border-[#8B263E]/40">
          <div className="flex items-center gap-2.5 min-w-0">
            <span className="bg-[#8B263E] text-white font-bold text-[11px] sm:text-xs uppercase px-2.5 py-1 rounded-md tracking-wider shrink-0">
              Genealogical Dossier
            </span>
            {activePerson.branchTag && (
              <span className="text-amber-200 text-xs sm:text-sm font-medium truncate">
                {activePerson.branchTag}
              </span>
            )}
          </div>

          <div className="flex items-center gap-2">
            {activePerson.bio && (
              <button
                onClick={handleToggleSpeakBio}
                className={`min-h-[44px] flex items-center gap-2 px-3 py-2 rounded-xl text-xs md:text-sm font-bold transition cursor-pointer shadow-xs ${
                  isPlaying ? 'bg-amber-500 text-black animate-pulse' : 'bg-[#3D302B] text-white hover:bg-[#8B263E]'
                }`}
                title="Read biographical text aloud"
              >
                {isPlaying ? (
                  <>
                    <VolumeX className="w-4 h-4 shrink-0" />
                    <span>Stop Voice</span>
                  </>
                ) : (
                  <>
                    <Volume2 className="w-4 h-4 text-amber-300 shrink-0" />
                    <span>Listen</span>
                  </>
                )}
              </button>
            )}

            <button
              onClick={handlePrintDossier}
              className="min-h-[44px] min-w-[44px] p-2.5 bg-[#3D302B] hover:bg-[#8B263E] text-white rounded-xl transition flex items-center justify-center cursor-pointer"
              title="Print Historical Dossier"
            >
              <Printer className="w-5 h-5" />
            </button>

            <button
              onClick={() => {
                if (isPlaying) stop();
                onClose();
              }}
              className="min-h-[44px] min-w-[44px] p-2 bg-white/20 hover:bg-red-700 text-white rounded-xl transition font-bold flex items-center justify-center cursor-pointer"
              aria-label="Close dossier"
            >
              <X className="w-6 h-6" />
            </button>
          </div>
        </div>

        {/* Modal Body / Printable Content */}
        <div className="p-4 sm:p-6 md:p-8 space-y-6 sm:space-y-8">
          
          {/* Main Hero Header */}
          <div className="flex flex-col sm:flex-row gap-5 sm:gap-6 items-center sm:items-start border-b border-[#D8CFC4] pb-6">
            
            {/* Avatar Column: Verified Historical Photo OR Archival Text Monogram Badge */}
            {hasPortrait && verifiedPhotoUrl ? (
              <div className="relative shrink-0">
                <img
                  src={verifiedPhotoUrl}
                  alt={`${activePerson.firstName} ${activePerson.lastName}`}
                  referrerPolicy="no-referrer"
                  className="w-32 h-32 sm:w-36 sm:h-36 md:w-44 md:h-44 object-cover rounded-xl border-4 border-[#8B263E] shadow-md bg-[#E8DEC9]"
                />
                <span className={`absolute -bottom-2 -right-2 px-3 py-1 rounded-full text-[11px] font-bold text-white shadow-xs ${
                  activePerson.isLiving ? 'bg-emerald-700' : 'bg-[#2C221E]'
                }`}>
                  {activePerson.isLiving ? 'Living Relative' : 'Archival Portrait'}
                </span>
              </div>
            ) : (
              <div className="relative shrink-0 flex flex-col items-center">
                <div className="w-28 h-28 sm:w-36 sm:h-36 md:w-40 md:h-40 rounded-2xl bg-gradient-to-br from-[#EFE8DD] to-[#DDD2C0] border-2 border-[#8B263E]/40 p-1.5 shadow-md flex items-center justify-center">
                  <div className="w-full h-full rounded-xl border border-dashed border-[#8B263E]/50 flex flex-col items-center justify-center p-3 text-center bg-[#FBF9F5]">
                    <span className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold tracking-widest text-[#8B263E]">
                      {initials}
                    </span>
                    <span className="text-[10px] uppercase font-bold tracking-wider text-[#6E5E55] mt-1 border-t border-[#D8CFC4] pt-1">
                      Archival Record
                    </span>
                  </div>
                </div>
                {activePerson.isLiving !== undefined && (
                  <span className={`mt-2 px-2.5 py-0.5 rounded-full text-[10px] font-bold text-white shadow-xs ${
                    activePerson.isLiving ? 'bg-emerald-700' : 'bg-[#2C221E]'
                  }`}>
                    {activePerson.isLiving ? 'Living Descendant' : 'In Memoriam'}
                  </span>
                )}
              </div>
            )}

            {/* Profile Identity & Strictly Extracted Facts */}
            <div className="space-y-2.5 text-center sm:text-left flex-1 min-w-0">
              <div>
                <h2 
                  id="dossier-title"
                  className="font-serif text-2xl sm:text-3xl md:text-4xl font-bold text-[#2C221E] break-words leading-tight"
                >
                  {activePerson.firstName} {activePerson.lastName}
                  {activePerson.maidenName && (
                    <span className="text-lg sm:text-xl md:text-2xl text-[#6E5E55] font-normal italic block sm:inline sm:ml-2">
                      (nee {activePerson.maidenName})
                    </span>
                  )}
                </h2>
                
                <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2 mt-1.5 text-xs sm:text-sm">
                  {activePerson.branchTag && (
                    <span className="inline-flex items-center gap-1 font-bold text-[#8B263E] bg-[#8B263E]/10 px-2.5 py-0.5 rounded-md">
                      <Layers className="w-3.5 h-3.5 shrink-0" />
                      {activePerson.branchTag}
                    </span>
                  )}
                  {activePerson.generation !== undefined && (
                    <span className="text-[#6E5E55] font-semibold">
                      • Generation {activePerson.generation}
                    </span>
                  )}
                </div>
              </div>

              {/* Strict Fact Extraction: Life Span, Birthplace, Occupation */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 text-xs sm:text-sm text-[#4A3E38] pt-2">
                {formattedLifeSpan && (
                  <div className="flex items-center gap-2 justify-center sm:justify-start bg-white/70 px-3 py-1.5 rounded-lg border border-[#E7E2D8]">
                    <Calendar className="w-4 h-4 text-[#8B263E] shrink-0" />
                    <span>
                      <strong className="text-[#2C221E]">Life Span:</strong> {formattedLifeSpan}
                    </span>
                  </div>
                )}

                {activePerson.birthPlace && activePerson.birthPlace.trim() !== '' && (
                  <div className="flex items-center gap-2 justify-center sm:justify-start bg-white/70 px-3 py-1.5 rounded-lg border border-[#E7E2D8]">
                    <MapPin className="w-4 h-4 text-[#8B263E] shrink-0" />
                    <span>
                      <strong className="text-[#2C221E]">Birthplace:</strong> {activePerson.birthPlace}
                    </span>
                  </div>
                )}

                {activePerson.deathPlace && activePerson.deathPlace.trim() !== '' && (
                  <div className="flex items-center gap-2 justify-center sm:justify-start bg-white/70 px-3 py-1.5 rounded-lg border border-[#E7E2D8]">
                    <MapPin className="w-4 h-4 text-[#8B263E] shrink-0" />
                    <span>
                      <strong className="text-[#2C221E]">Resting Place:</strong> {activePerson.deathPlace}
                    </span>
                  </div>
                )}

                {activePerson.occupation && activePerson.occupation.trim() !== '' && (
                  <div className="flex items-center gap-2 justify-center sm:justify-start sm:col-span-2 bg-white/70 px-3 py-1.5 rounded-lg border border-[#E7E2D8]">
                    <Briefcase className="w-4 h-4 text-[#8B263E] shrink-0" />
                    <span>
                      <strong className="text-[#2C221E]">Occupation:</strong> {activePerson.occupation}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Life Biography (Only rendered if present in source text) */}
          {activePerson.bio && activePerson.bio.trim() !== '' && (
            <div className="bg-white p-4 sm:p-5 md:p-6 rounded-xl border-2 border-[#E7E2D8] space-y-2.5 shadow-xs">
              <h3 className="font-serif text-lg sm:text-xl font-bold text-[#8B263E] flex items-center gap-2">
                <BookOpen className="w-5 h-5 text-[#8B263E] shrink-0" />
                <span>Life Biography &amp; Documentary Record</span>
              </h3>
              <p className="text-sm sm:text-base md:text-lg leading-relaxed text-[#2C221E] break-words whitespace-pre-line font-serif">
                {activePerson.bio}
              </p>
            </div>
          )}

          {/* Family Connections: Interactive Connection Nodes */}
          {hasAnyConnections && (
            <div className="space-y-4">
              <h3 className="font-serif text-lg sm:text-xl font-bold text-[#2C221E] border-b-2 border-[#2C221E] pb-2 flex items-center gap-2">
                <Users className="w-5 h-5 text-[#8B263E]" />
                <span>Family Connections</span>
              </h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                
                {/* Parents Nodes (Dynamically omitted if neither exists) */}
                {(father || mother) && (
                  <div className="bg-[#F2ECE1] p-4 rounded-xl border border-[#D8CFC4] space-y-2.5">
                    <h4 className="text-xs uppercase font-bold text-[#8B263E] tracking-wider">Parents</h4>
                    
                    {father && (
                      <button
                        onClick={() => onSelectMember(father.id)}
                        className="min-h-[44px] w-full text-left p-2.5 bg-white rounded-lg border border-[#D8CFC4] hover:border-[#8B263E] hover:shadow-xs transition flex items-center justify-between text-xs sm:text-sm font-semibold cursor-pointer group"
                      >
                        <div>
                          <span className="text-[10px] uppercase font-bold text-[#8B263E] block">Father</span>
                          <span className="text-[#2C221E] group-hover:text-[#8B263E] transition">
                            {father.firstName} {father.lastName}
                          </span>
                          {father.birthYear && (
                            <span className="text-[11px] text-[#6E5E55] block font-normal">
                              ({father.birthYear} – {father.deathYear || 'Present'})
                            </span>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#8B263E] shrink-0 group-hover:translate-x-0.5 transition" />
                      </button>
                    )}

                    {mother && (
                      <button
                        onClick={() => onSelectMember(mother.id)}
                        className="min-h-[44px] w-full text-left p-2.5 bg-white rounded-lg border border-[#D8CFC4] hover:border-[#8B263E] hover:shadow-xs transition flex items-center justify-between text-xs sm:text-sm font-semibold cursor-pointer group"
                      >
                        <div>
                          <span className="text-[10px] uppercase font-bold text-[#8B263E] block">Mother</span>
                          <span className="text-[#2C221E] group-hover:text-[#8B263E] transition">
                            {mother.firstName} {mother.lastName}
                            {mother.maidenName && ` (nee ${mother.maidenName})`}
                          </span>
                          {mother.birthYear && (
                            <span className="text-[11px] text-[#6E5E55] block font-normal">
                              ({mother.birthYear} – {mother.deathYear || 'Present'})
                            </span>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#8B263E] shrink-0 group-hover:translate-x-0.5 transition" />
                      </button>
                    )}
                  </div>
                )}

                {/* Spouse / Partner Nodes (Dynamically omitted if none) */}
                {spouses.length > 0 && (
                  <div className="bg-[#F2ECE1] p-4 rounded-xl border border-[#D8CFC4] space-y-2.5">
                    <h4 className="text-xs uppercase font-bold text-[#8B263E] tracking-wider flex items-center gap-1.5">
                      <Heart className="w-3.5 h-3.5 text-[#8B263E]" />
                      <span>Spouse / Partner ({spouses.length})</span>
                    </h4>
                    {spouses.map(s => (
                      <button
                        key={s.id}
                        onClick={() => onSelectMember(s.id)}
                        className="min-h-[44px] w-full text-left p-2.5 bg-white rounded-lg border border-[#D8CFC4] hover:border-[#8B263E] hover:shadow-xs transition flex items-center justify-between text-xs sm:text-sm font-semibold cursor-pointer group"
                      >
                        <div>
                          <span className="text-[#2C221E] group-hover:text-[#8B263E] transition">
                            {s.firstName} {s.lastName}
                            {s.maidenName && ` (nee ${s.maidenName})`}
                          </span>
                          {s.birthYear && (
                            <span className="text-[11px] text-[#6E5E55] block font-normal">
                              ({s.birthYear} – {s.deathYear || 'Present'})
                            </span>
                          )}
                        </div>
                        <ChevronRight className="w-4 h-4 text-[#8B263E] shrink-0 group-hover:translate-x-0.5 transition" />
                      </button>
                    ))}
                  </div>
                )}

                {/* Children Nodes (Dynamically omitted if none) */}
                {children.length > 0 && (
                  <div className="bg-[#F2ECE1] p-4 rounded-xl border border-[#D8CFC4] space-y-2.5">
                    <h4 className="text-xs uppercase font-bold text-[#8B263E] tracking-wider">
                      Children ({children.length})
                    </h4>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {children.map(c => (
                        <button
                          key={c.id}
                          onClick={() => onSelectMember(c.id)}
                          className="min-h-[44px] w-full text-left p-2 bg-white rounded-lg border border-[#D8CFC4] hover:border-[#8B263E] hover:shadow-xs transition flex items-center justify-between text-xs font-semibold cursor-pointer group"
                        >
                          <div>
                            <span className="text-[#2C221E] group-hover:text-[#8B263E] transition">
                              {c.firstName} {c.lastName}
                            </span>
                            {c.birthYear && (
                              <span className="text-[10px] text-[#6E5E55] block font-normal">
                                b. {c.birthYear}
                              </span>
                            )}
                          </div>
                          <ChevronRight className="w-3.5 h-3.5 text-[#8B263E] shrink-0 group-hover:translate-x-0.5 transition" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Siblings Nodes (Dynamically omitted if none) */}
                {siblings.length > 0 && (
                  <div className="bg-[#F2ECE1] p-4 rounded-xl border border-[#D8CFC4] space-y-2.5">
                    <h4 className="text-xs uppercase font-bold text-[#8B263E] tracking-wider">
                      Siblings ({siblings.length})
                    </h4>
                    <div className="space-y-1.5 max-h-48 overflow-y-auto pr-1">
                      {siblings.map(sib => (
                        <button
                          key={sib.id}
                          onClick={() => onSelectMember(sib.id)}
                          className="min-h-[44px] w-full text-left p-2 bg-white rounded-lg border border-[#D8CFC4] hover:border-[#8B263E] hover:shadow-xs transition flex items-center justify-between text-xs font-semibold cursor-pointer group"
                        >
                          <div>
                            <span className="text-[#2C221E] group-hover:text-[#8B263E] transition">
                              {sib.firstName} {sib.lastName}
                            </span>
                            {sib.birthYear && (
                              <span className="text-[10px] text-[#6E5E55] block font-normal">
                                ({sib.birthYear} – {sib.deathYear || 'Present'})
                              </span>
                            )}
                          </div>
                          <ChevronRight className="w-3.5 h-3.5 text-[#8B263E] shrink-0 group-hover:translate-x-0.5 transition" />
                        </button>
                      ))}
                    </div>
                  </div>
                )}

              </div>
            </div>
          )}

          {/* Chronological Life Milestones Timeline (Only if present) */}
          {activePerson.lifeEvents && activePerson.lifeEvents.length > 0 && (
            <div className="space-y-4">
              <h3 className="font-serif text-xl font-bold text-[#2C221E] border-b-2 border-[#2C221E] pb-2">
                Historical Life Milestones
              </h3>

              <div className="relative pl-6 space-y-5 before:absolute before:left-2.5 before:top-2 before:bottom-2 before:w-1 before:bg-[#8B263E]/30">
                {activePerson.lifeEvents.map(event => (
                  <div key={event.id} className="relative bg-white p-4 rounded-xl border border-[#D8CFC4] shadow-xs">
                    <div className="absolute -left-[1.85rem] top-4 w-4 h-4 rounded-full bg-[#8B263E] border-2 border-white" />
                    <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                      <span className="font-serif text-base sm:text-lg font-bold text-[#8B263E]">
                        {event.year} — {event.title}
                      </span>
                      {event.location && (
                        <span className="text-xs bg-[#F2ECE1] px-2.5 py-0.5 rounded-full font-bold text-[#6E5E55]">
                          📍 {event.location}
                        </span>
                      )}
                    </div>
                    <p className="text-xs sm:text-sm text-[#4A3E38] leading-relaxed">
                      {event.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* SECTION 4: SOURCE LINEAGE & HISTORICAL DOCUMENTS */}
          {hasSourceLineage && (
            <div className="space-y-4 pt-2">
              <div className="border-b-2 border-[#2C221E] pb-2 flex items-center justify-between">
                <h3 className="font-serif text-xl font-bold text-[#2C221E] flex items-center gap-2">
                  <FileText className="w-5 h-5 text-[#8B263E]" />
                  <span>Source Lineage &amp; Historical Documents</span>
                </h3>
                <span className="text-xs text-[#6E5E55] font-semibold">
                  Archival Citations
                </span>
              </div>

              {/* 1. Explicit Source References from Website Chapters */}
              {sourceRefs.length > 0 && (
                <div className="space-y-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#8B263E]">
                    Referenced in Site Sections &amp; Chapters
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {sourceRefs.map((ref, idx) => (
                      <div 
                        key={idx}
                        className="p-3.5 bg-white rounded-xl border border-[#D8CFC4] hover:border-[#8B263E] transition shadow-xs flex flex-col justify-between"
                      >
                        <div>
                          <div className="flex items-center justify-between gap-2">
                            <span className="font-serif font-bold text-sm text-[#2C221E]">
                              {ref.title}
                            </span>
                            {ref.tab && (
                              <span className="text-[10px] bg-[#8B263E]/10 text-[#8B263E] font-bold px-2 py-0.5 rounded">
                                Website Chapter
                              </span>
                            )}
                          </div>
                          {ref.description && (
                            <p className="text-xs text-[#6E5E55] mt-1.5 leading-relaxed">
                              {ref.description}
                            </p>
                          )}
                        </div>

                        {ref.tab && onSelectTab && (
                          <div className="mt-3 pt-2 border-t border-[#F2ECE1]">
                            <button
                              onClick={() => {
                                onClose();
                                onSelectTab(ref.tab!);
                              }}
                              className="inline-flex items-center gap-1.5 text-xs font-bold text-[#8B263E] hover:underline cursor-pointer"
                            >
                              <span>Open Document Section</span>
                              <ExternalLink className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 2. Linked Stories */}
              {personStories.length > 0 && (
                <div className="space-y-2 pt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#8B263E]">
                    Linked Archival Stories ({personStories.length})
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {personStories.map(story => (
                      <div
                        key={story.id}
                        onClick={() => {
                          onClose();
                          if (onSelectStory) {
                            onSelectStory(story.id);
                          }
                        }}
                        className="p-3.5 bg-white rounded-xl border-2 border-[#E7E2D8] hover:border-[#8B263E] cursor-pointer transition shadow-xs group"
                      >
                        <h5 className="font-serif text-sm sm:text-base font-bold text-[#2C221E] group-hover:text-[#8B263E] transition">
                          {story.title}
                        </h5>
                        <p className="text-xs text-[#6E5E55] mt-1 line-clamp-2">
                          {story.summary}
                        </p>
                        <span className="inline-flex items-center gap-1 mt-2 text-xs font-bold text-[#8B263E] underline">
                          Read Document &rarr;
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 3. Linked Historical Photographs */}
              {personPhotos.length > 0 && (
                <div className="space-y-2 pt-2">
                  <h4 className="text-xs font-bold uppercase tracking-wider text-[#8B263E]">
                    Archived Photographs &amp; Artifacts ({personPhotos.length})
                  </h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                    {personPhotos.map(photo => (
                      <div key={photo.id} className="group relative rounded-lg overflow-hidden border border-[#D8CFC4] bg-white">
                        <img
                          src={photo.fileUrl}
                          alt={photo.title}
                          referrerPolicy="no-referrer"
                          className="w-full h-28 object-cover group-hover:scale-105 transition duration-300"
                        />
                        <div className="p-2 text-xs">
                          <p className="font-bold truncate text-[#2C221E]">{photo.title}</p>
                          <p className="text-[10px] text-[#6E5E55]">{photo.year || photo.decade}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

            </div>
          )}

        </div>

        {/* Footer Bar */}
        <div className="p-4 bg-[#F2ECE1] border-t border-[#D8CFC4] text-center text-xs text-[#6E5E55] no-print">
          Historical Record ID: {activePerson.id} • Denton • Roberts • Gibb • Giles • Negus Family Archive
        </div>
      </div>
    </div>
  );
};

// Aliases for compatibility
export { PersonDossierModal as PersonDossier };
export { PersonDossierModal as MemberDossierModal };
